'use client';

import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import useScrollReveal from '@/hooks/useScrollReveal';

interface Pillar {
  title: string;
  subtitle: string;
  description: string;
  services: string[];
  icon: string;
  accentColor: string;
  image: string;
  imageAlt: string;
}

export default function WhatWeBuild() {
  const { ref: sectionRef, isVisible: sectionVisible } = useScrollReveal<HTMLElement>();

  const pillars: Pillar[] = [
  {
    title: 'CLARITY',
    subtitle: 'Foundation First',
    description: "Most founders skip this step and wonder why their marketing doesn't work. You can't sell what you can't explain. We start here—positioning, messaging, brand identity. The foundation everything else is built on.",
    services: ['Brand positioning', 'Messaging architecture', 'Identity systems'],
    icon: 'SparklesIcon',
    accentColor: 'brand-teal',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e8ee3ea4-1772111005147.png",
    imageAlt: 'Team developing brand strategy with sketches and creative materials in industrial workspace'
  },
  {
    title: 'BUILD',
    subtitle: 'Digital Presence',
    description: 'Your website is either making you money or costing you opportunities. We build fast, functional sites designed to convert—not just look pretty. Mobile-responsive, SEO-optimized, built to handle growth.',
    services: ['Websites', 'Platforms', 'Digital presence', 'Design'],
    icon: 'ComputerDesktopIcon',
    accentColor: 'brand-green',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e82939f4-1772501084703.png",
    imageAlt: 'Designer coding modern website with multiple monitors in creative studio'
  },
  {
    title: 'MOBILE',
    subtitle: 'Apps That Work in the Field',
    description: 'Your customers and crew are on their phones. We build mobile apps and PWAs that work where your business actually happens—in the field, on the road, offline. Flutter, React Native, and Progressive Web Apps built for real-world use.',
    services: ['Flutter apps', 'React Native', 'Progressive Web Apps', 'Field tools'],
    icon: 'DevicePhoneMobileIcon',
    accentColor: 'brand-plum',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_158a88d53-1772422337178.png",
    imageAlt: 'Mobile app development with smartphone showing field service application'
  },
  {
    title: 'SYSTEMS',
    subtitle: 'Operational Infrastructure',
    description: 'Growth without systems creates chaos. Missed follow-ups. Lost leads. Manual work eating 20 hours a week. We build the operational infrastructure that lets you scale without burning out—automation, workflows, CRM integration.',
    services: ['Automation', 'Workflows', 'CRM integration', 'Operational infrastructure'],
    icon: 'CogIcon',
    accentColor: 'brand-orange',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_194625d93-1772217102353.png",
    imageAlt: 'Automated workflow systems and operational dashboard in modern workspace'
  }];


  return (
    <section
      id="what-we-build"
      ref={sectionRef}
      className={`py-12 sm:py-16 md:py-24 lg:py-32 bg-white transition-all duration-1000 ${
      sectionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`
      }>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
        <div className={`text-center mb-10 sm:mb-12 md:mb-16 transition-all duration-1000 ${sectionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h2 className="font-headline text-3xl sm:text-4xl md:text-5xl text-brand-plum mb-3 sm:mb-4 font-bold leading-tight">
            What We Build
          </h2>
          <p className="font-body text-sm sm:text-base md:text-lg text-text-secondary max-w-3xl mx-auto leading-relaxed px-4">
            Three pillars. Every project. No exceptions.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
          {pillars.map((pillar, index) => {
            const { ref: pillarRef, isVisible: pillarVisible } = useScrollReveal<HTMLDivElement>();
            return (
              <div
                key={index}
                ref={pillarRef}
                className={`group relative overflow-hidden transition-all duration-700 hover:shadow-2xl ${pillarVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${index * 150}ms` }}>
            
              {/* Background image */}
              <div className="relative h-[450px] sm:h-[500px] md:h-[550px] overflow-hidden">
                <AppImage
                    src={pillar.image}
                    alt={pillar.imageAlt}
                    width={800}
                    height={550}
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700" />
              
                
                {/* Dark overlay */}
                <div className="absolute inset-0 bg-black/70 group-hover:bg-black/60 transition-colors duration-500"></div>
                
                {/* Content overlay */}
                <div className="absolute inset-0 flex flex-col justify-between p-4 sm:p-6">
                  <div>
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 bg-${pillar.accentColor} flex items-center justify-center mb-3 sm:mb-4 transform group-hover:scale-110 transition-transform duration-300`}>
                      <Icon name={pillar.icon} size={20} className="text-white sm:w-6 sm:h-6" />
                    </div>
                    <div className={`inline-block px-2 py-1 sm:px-3 sm:py-1 bg-${pillar.accentColor} text-white text-[10px] sm:text-xs font-body font-bold mb-2 sm:mb-3 uppercase tracking-wider`}>
                      {pillar.subtitle}
                    </div>
                    <h3 className="font-headline text-2xl sm:text-3xl text-white mb-2 sm:mb-3 font-bold leading-tight tracking-wide">
                      {pillar.title}
                    </h3>
                    <p className="font-body text-xs sm:text-sm text-white/90 leading-relaxed mb-3 sm:mb-4">
                      {pillar.description}
                    </p>
                  </div>
                  
                  <div>
                    <div className="border-t border-white/30 pt-3 sm:pt-4">
                      <ul className="space-y-1.5 sm:space-y-2">
                        {pillar.services.map((service, idx) =>
                          <li key={idx} className="flex items-center gap-2 text-white text-xs sm:text-sm">
                          <span className={`w-1.5 h-1.5 bg-${pillar.accentColor} rounded-full`}></span>
                          {service}
                        </li>
                          )}
                      </ul>
                    </div>
                  </div>
                </div>
                
                {/* Accent bar */}
                <div className={`absolute top-0 left-0 w-1 h-full bg-${pillar.accentColor} group-hover:w-2 transition-all duration-300`}></div>
              </div>
            </div>);

          })}
        </div>
      </div>
    </section>);

}